import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Entry } from 'contentful'
import { IArticleFields, IPodcastEpisodeFields, isDocument } from '@estercare/ester-shared'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { useGetEntriesByContentType } from '../api/contentful/contentfulQueries'
import { useGetUserHealthPlanOutcomesQuery } from '../api/ester-b2b-api/private/healthPlan/healthPlanQueries'
import { useSelectUserMarket } from '../state/selectors'
import { isSwedishMarket } from '../utils/market'
import { useRotatingContent } from '../hooks/useRotatingContent'
import { findRelatedContentBasedOnOutcome } from '../utils/outcomeToHelpAreas'
import { CollectionCard } from './CollectionCard'
import { BlockItem } from './BlockItem'
import { ComponentLayout } from './layouts/ComponentLayout'
import { Title6 } from './styles/Typography'
import { LinkButton } from './buttons/link/LinkButton'
import { theme } from '../theme/theme'
import { parseContenfulImageUrl } from '../contentful/utils'

const DEFAULT_IMAGE = '/images/stock1.png'

const getArticleImage = (article: IArticleFields): string => {
  if (!article.primaryImage) return DEFAULT_IMAGE
  return parseContenfulImageUrl(article.primaryImage, { width: 600, height: 400 })
}

const getPodcastImage = (podcast: IPodcastEpisodeFields): string => {
  if (!podcast.featuredImage) return DEFAULT_IMAGE
  return parseContenfulImageUrl(podcast.featuredImage, { width: 600, height: 400 })
}

const getPodcastAbstract = (podcast: IPodcastEpisodeFields): string => {
  if (!podcast.description) return ''
  return isDocument(podcast.description) ? documentToPlainTextString(podcast.description) : ''
}

const ArticleCard = ({ entry, isHorizontal }: { entry: Entry<IArticleFields>; isHorizontal?: boolean }) => {
  const { t } = useTranslation()
  return (
    <CollectionCard
      horizontal={isHorizontal}
      slug={`/portal/resource-center/articles/${entry.fields.slug}`}
      title={entry.fields.title || ''}
      abstract={entry.fields.abstract || ''}
      label={t('common.content.article').toUpperCase()}
      image={{
        url: getArticleImage(entry.fields),
        description: entry.fields.title,
      }}
    />
  )
}

const PodcastCard = ({ entry }: { entry: Entry<IPodcastEpisodeFields> }) => {
  const { t } = useTranslation()
  return (
    <CollectionCard
      slug={`/portal/resource-center/podcasts/${entry.fields.slug}`}
      title={entry.fields.title || ''}
      abstract={getPodcastAbstract(entry.fields)}
      label={t('common.content.podcast').toUpperCase()}
      image={{
        url: getPodcastImage(entry.fields),
        description: entry.fields.title,
      }}
    />
  )
}

export const ArticlesAndPodcastsSection = () => {
  const { t } = useTranslation()
  const userMarket = useSelectUserMarket()
  const isSwedish = isSwedishMarket(userMarket)

  const articleEntries = useGetEntriesByContentType<IArticleFields>('article').data?.items
  const podcastEntries = useGetEntriesByContentType<IPodcastEpisodeFields>('podcastEpisode').data?.items
  const { data: outcomesData } = useGetUserHealthPlanOutcomesQuery()

  const allArticles = articleEntries?.filter((entry) => !entry.fields.isMiniArticle)
  const outcomes = isSwedish ? outcomesData?.outcomes : undefined
  const { matched: matchedArticles, unmatched: unmatchedArticles } = allArticles
    ? findRelatedContentBasedOnOutcome(allArticles, outcomes)
    : { matched: [], unmatched: [] }
  const { matched: matchedPodcasts, unmatched: unmatchedPodcasts } = podcastEntries
    ? findRelatedContentBasedOnOutcome(podcastEntries, outcomes)
    : { matched: [], unmatched: [] }

  const rotatedMatchedArticles = useRotatingContent(matchedArticles, matchedArticles.length)
  const rotatedUnmatchedArticles = useRotatingContent(unmatchedArticles, unmatchedArticles.length)
  const allRankedArticles = [...rotatedMatchedArticles, ...rotatedUnmatchedArticles]

  const horizontalArticles = allRankedArticles.slice(0, 2)
  const verticalArticleCount = isSwedish ? 1 : 4
  const verticalArticles = allRankedArticles.slice(2, 2 + verticalArticleCount)

  const rotatedMatchedPodcasts = useRotatingContent(matchedPodcasts, matchedPodcasts.length)
  const rotatedUnmatchedPodcasts = useRotatingContent(unmatchedPodcasts, unmatchedPodcasts.length)
  const podcasts = [...rotatedMatchedPodcasts, ...rotatedUnmatchedPodcasts].slice(0, 3)

  const hasContent = horizontalArticles.length > 0 || verticalArticles.length > 0

  if (!hasContent) return null

  return (
    <ComponentLayout paddingBottom={0} paddingTop={theme.spacing.xxlarge}>
      <Title6>{t('employee.home.digDeeperTitle')}</Title6>

      {horizontalArticles.length > 0 && (
        <HorizontalRow>
          {horizontalArticles.map((entry) => (
            <BlockItem key={entry.sys.id} height={215} gridColumn="span 1">
              <ArticleCard entry={entry} isHorizontal />
            </BlockItem>
          ))}
        </HorizontalRow>
      )}

      <VerticalRow>
        {verticalArticles.map((entry) => (
          <BlockItem key={entry.sys.id}>
            <ArticleCard entry={entry} />
          </BlockItem>
        ))}
        {isSwedish &&
          podcasts.map((entry) => (
            <BlockItem key={entry.sys.id}>
              <PodcastCard entry={entry} />
            </BlockItem>
          ))}
      </VerticalRow>

      <ButtonWrapper>
        <LinkButton href="/portal/resource-center" text={t('employee.home.discoverMore')} secondary />
      </ButtonWrapper>
    </ComponentLayout>
  )
}

const HorizontalRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.medium}px;
  margin-top: ${theme.spacing.large}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: 1fr;
  }
`

const VerticalRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.medium}px;
  margin-top: ${theme.spacing.medium}px;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${theme.breakpoint.small}px) {
    grid-template-columns: 1fr;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${theme.spacing.large}px;
`
